import { logger } from "@/lib/logger";
import { getToken } from "@/services/microsoft/auth";
import type { Item } from "@/pages/workspace/utils/workspaceItemUtils";

const GRAPH_RESOURCE = "https://graph.microsoft.com";

/**
 * The scopes a rename needs — the same two "Upload til OneDrive" already acquires, so
 * this adds no permission the app does not already ask for. Whatever consent a tenant
 * has granted for upload, a rename inherits.
 */
export const GRAPH_WRITE_SCOPES = ["Files.ReadWrite", "Files.ReadWrite.All"];

/** Why a cloud rename did not happen. Each maps to one Danish line in {@link CLOUD_RENAME_MESSAGES}. */
export type CloudRenameFailure =
  /** The document carries no drive/item id, so there is nothing in the cloud to address. */
  | "missingCloudReference"
  /** No Graph token with write scope — the tenant blocks consent, or the user declined. */
  | "consentUnavailable"
  /** Graph 401/403: this user may not write this file. */
  | "accessDenied"
  /** Graph 404: the cloud file is gone or unreachable. */
  | "notFound"
  /** Graph 409: OneDrive/SharePoint already hold a file of that name in the folder. */
  | "nameConflict"
  /** Graph 400: the name is one OneDrive/SharePoint refuse. */
  | "invalidName"
  | "failed";

export type CloudRenameResult =
  | { ok: true }
  | {
      ok: false;
      reason: CloudRenameFailure;
      status?: number;
      graphErrorCode?: string;
    };

export const CLOUD_RENAME_MESSAGES: Record<CloudRenameFailure, string> = {
  missingCloudReference:
    "Filen mangler sin reference til OneDrive/SharePoint og kan ikke omdøbes der.",
  consentUnavailable:
    "Kunne ikke logge på OneDrive/SharePoint. Prøv at logge ud og ind igen.",
  accessDenied:
    "Du har ikke rettigheder til at omdøbe filen i OneDrive/SharePoint.",
  notFound: "Filen blev ikke fundet i OneDrive/SharePoint.",
  nameConflict:
    "Der findes allerede en fil med dette navn i OneDrive/SharePoint.",
  invalidName: "OneDrive/SharePoint afviser navnet. Prøv et andet navn.",
  failed: "Kunne ikke omdøbe filen i OneDrive/SharePoint.",
};

/** A drive item is addressable by its drive id alone, for OneDrive and SharePoint alike. */
export const buildGraphDriveItemUrl = (
  driveId: string,
  itemId: string
): string =>
  `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}`;

const reasonForStatus = (status: number): CloudRenameFailure => {
  if (status === 401 || status === 403) return "accessDenied";
  if (status === 404) return "notFound";
  if (status === 409) return "nameConflict";
  if (status === 400) return "invalidName";
  return "failed";
};

const readGraphErrorCode = async (
  response: Response
): Promise<string | undefined> => {
  const text = await response.text().catch(() => "");
  if (!text) return undefined;
  try {
    const parsed = JSON.parse(text) as { error?: { code?: unknown } };
    return typeof parsed.error?.code === "string"
      ? parsed.error.code
      : undefined;
  } catch {
    // Do not log the raw body — it may carry user or content details.
    return undefined;
  }
};

/**
 * Renames a drive item via Microsoft Graph (`PATCH` with the new `name`). Permission is
 * whatever Graph decides for `token`'s user on this item — a 403 is the authoritative
 * "no", so nothing is pre-checked. `@microsoft.graph.conflictBehavior` is left at its
 * default, `fail`, so a taken name comes back as a 409 rather than being silently
 * suffixed into something the user did not type.
 */
export const renameGraphDriveItem = async (
  driveId: string,
  itemId: string,
  newName: string,
  token: string
): Promise<CloudRenameResult> => {
  const operation = "renameGraphDriveItem";

  try {
    const response = await fetch(buildGraphDriveItemUrl(driveId, itemId), {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name: newName }),
    });

    if (response.ok) return { ok: true };

    const graphErrorCode = await readGraphErrorCode(response);
    logger.warn("Microsoft Graph refused to rename a drive item", {
      operation,
      driveId,
      itemId,
      status: response.status,
      statusText: response.statusText,
      graphErrorCode,
    });
    return {
      ok: false,
      reason: reasonForStatus(response.status),
      status: response.status,
      graphErrorCode,
    };
  } catch (error) {
    logger.error("Failed to reach Microsoft Graph to rename a drive item", {
      operation,
      error,
      driveId,
      itemId,
    });
    return { ok: false, reason: "failed" };
  }
};

/**
 * Renames a cloud-linked workspace document in OneDrive/SharePoint with the user's own
 * delegated Graph token. Called before the workspace rename: the cloud copy is the
 * source of truth for a linked file (sync mirrors its name back), so the cloud must
 * change first — if it refuses, nothing has changed anywhere.
 */
export const renameCloudDocument = async (
  item: Pick<Item, "id" | "driveId" | "sharepointFileId">,
  newName: string
): Promise<CloudRenameResult> => {
  if (!item.driveId || !item.sharepointFileId) {
    logger.warn("Cloud-linked document has no drive item to rename", {
      operation: "renameCloudDocument",
      documentId: item.id,
      hasDriveId: Boolean(item.driveId),
      hasSharepointFileId: Boolean(item.sharepointFileId),
    });
    return { ok: false, reason: "missingCloudReference" };
  }

  let token: string;
  try {
    token = await getToken({
      type: "Graph",
      resource: GRAPH_RESOURCE,
      scopes: GRAPH_WRITE_SCOPES,
    });
  } catch (error) {
    // Expected while the tenant requires admin consent for the write scope, or the user
    // dismissed the popup — the same limit "Upload til OneDrive" runs into.
    logger.warn(
      "No Microsoft Graph write token for renaming a cloud document",
      {
        operation: "renameCloudDocument",
        documentId: item.id,
        error,
      }
    );
    return { ok: false, reason: "consentUnavailable" };
  }

  return renameGraphDriveItem(
    item.driveId,
    item.sharepointFileId,
    newName,
    token
  );
};
